import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeOffIcon,
  Loader2Icon,
  LockIcon,
  ShieldIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'

import api from '@nebula/shared/services/api'
import { usePortalStore } from '@nebula/shared/store'

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { theme, isPortalDomain } = usePortalStore()

  const primaryColor = isPortalDomain && theme ? theme.primaryColor : '#9333ea'
  const portalName = isPortalDomain && theme ? theme.portalName : 'Nebvla Cloud'

  const backgroundStyle =
    isPortalDomain && theme
      ? theme.backgroundImageUrl
        ? {
            backgroundImage: `url(${theme.backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : {
            backgroundColor: '#030712',
            backgroundImage: `radial-gradient(ellipse at 50% 40%, ${theme.secondaryColor}25 0%, transparent 60%)`,
          }
      : undefined

  const validatePassword = (value: string): string | null => {
    if (value.length < 8) return 'A senha deve ter pelo menos 8 caracteres'
    if (!/[A-Z]/.test(value)) return 'A senha deve conter pelo menos uma letra maiúscula'
    if (!/[a-z]/.test(value)) return 'A senha deve conter pelo menos uma letra minúscula'
    if (!/\d/.test(value)) return 'A senha deve conter pelo menos um número'
    return null
  }

  const passwordError = useMemo(() => {
    if (!password) return null
    return validatePassword(password)
  }, [password])

  if (!token) {
    return <Navigate to="/login" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('As senhas não coincidem')
      return
    }

    const validationError = validatePassword(password)
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)

    try {
      await api.post('/api/v1/auth/reset-password', {
        token,
        new_password: password,
      })
      setSuccess(true)
    } catch (error: any) {
      const message =
        error?.response?.data?.detail || 'Erro ao redefinir senha. O link pode ter expirado.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center ${isPortalDomain && theme ? '' : 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900'}`}
        style={backgroundStyle}
      >
        <div className="w-full max-w-md p-4">
          <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-lg">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-green-600/20 p-4">
                <CheckCircleIcon className="size-12 text-green-500" />
              </div>
            </div>

            <h1 className="mb-3 text-center text-2xl font-bold text-white">Senha alterada</h1>
            <p className="mb-8 text-center text-gray-300">
              Sua senha foi atualizada com sucesso. Agora você pode fazer login novamente.
            </p>

            <Link
              to="/login"
              className="flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
            >
              Ir para o login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`flex min-h-screen items-center justify-center ${isPortalDomain && theme ? '' : 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900'}`}
      style={backgroundStyle}
    >
      <div className="w-full max-w-md p-4">
        <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-lg">
          <div className="mb-6 flex justify-center">
            {isPortalDomain && theme?.logoUrl ? (
              <img
                src={theme.logoUrl}
                alt={portalName}
                className="max-h-24 w-auto max-w-[280px] object-contain"
              />
            ) : (
              <div className="rounded-full p-4" style={{ backgroundColor: `${primaryColor}33` }}>
                <ShieldIcon className="size-12" style={{ color: primaryColor }} />
              </div>
            )}
          </div>

          <h1 className="mb-2 text-center text-2xl font-bold text-white">Redefinir senha</h1>
          <p className="mb-8 text-center text-gray-300">Digite e confirme sua nova senha.</p>

          {error && (
            <div className="mb-6 flex items-center gap-3 rounded-lg border border-red-500/40 bg-red-500/20 p-4">
              <AlertCircleIcon className="size-5 shrink-0 text-red-300" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-200">Nova senha</label>
              <div className="relative">
                <LockIcon className="absolute top-1/2 left-4 size-5 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-lg border border-white/20 bg-white/10 px-12 py-3 text-white placeholder-gray-400 focus:ring-2 focus:outline-none"
                  style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? (
                    <EyeOffIcon className="size-5" />
                  ) : (
                    <EyeIcon className="size-5" />
                  )}
                </button>
              </div>
              {password && (
                <p
                  className={`mt-2 text-xs ${passwordError ? 'text-yellow-300' : 'text-green-400'}`}
                >
                  {passwordError || 'Senha válida'}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-200">
                Confirmar nova senha
              </label>
              <div className="relative">
                <LockIcon className="absolute top-1/2 left-4 size-5 -translate-y-1/2 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full rounded-lg border border-white/20 bg-white/10 px-12 py-3 text-white placeholder-gray-400 focus:ring-2 focus:outline-none"
                  style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showConfirmPassword ? (
                    <EyeOffIcon className="size-5" />
                  ) : (
                    <EyeIcon className="size-5" />
                  )}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-2 text-xs text-red-300">As senhas não coincidem</p>
              )}
              {confirmPassword && password === confirmPassword && (
                <p className="mt-2 text-xs text-green-400">Senhas coincidem</p>
              )}
            </div>

            <button
              type="submit"
              disabled={
                loading ||
                !password ||
                !confirmPassword ||
                password !== confirmPassword ||
                !!passwordError
              }
              className="flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? (
                <>
                  <Loader2Icon className="size-5 animate-spin" />
                  Redefinindo...
                </>
              ) : (
                'Redefinir senha'
              )}
            </button>
          </form>

          <div className="mt-6">
            <Link
              to="/login"
              className="flex w-full items-center justify-center gap-2 py-3 text-gray-300 transition-colors hover:text-white"
            >
              <ArrowLeftIcon className="size-4" />
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage
